import PasswordAuthProvider from './PasswordAuthProvider'
import EmailTokenAuthProvider from './EmailTokenAuthProvider'
import GoogleAuthProvider from './GoogleAuthProvider'
import FacebookAuthProvider from './FacebookAuthProvider'
import AppleAuthProvider from './AppleAuthProvider'
import LdapAuthProvider from './LdapAuthProvider'
import {AuthProviders, ProviderPriority, SocialProviders} from '@/domain/login/constant/authProviders'

/**
 * Factory for creating and managing authentication providers
 */
class AuthProviderFactory {
    constructor() {
        this.providers = new Map()
        this.service = null
    }

    /**
     * Initialize factory with login service
     * @param {Object} service - Login service instance
     */
    init(service) {
        this.service = service

        // Register all available providers
        this.providers.set(AuthProviders.PASSWORD, new PasswordAuthProvider(service))
        this.providers.set(AuthProviders.EMAIL_TOKEN, new EmailTokenAuthProvider(service))
        this.providers.set(AuthProviders.GOOGLE, new GoogleAuthProvider(service))
        this.providers.set(AuthProviders.FACEBOOK, new FacebookAuthProvider(service))
        this.providers.set(AuthProviders.APPLE, new AppleAuthProvider(service))
        this.providers.set(AuthProviders.LDAP, new LdapAuthProvider(service))
    }

    /**
     * Get provider by method name
     * @param {string} method - 'password', 'email_token', 'google', 'facebook', 'apple', 'ldap'
     * @returns {BaseAuthProvider}
     */
    getProvider(method) {
        const provider = this.providers.get(method)

        if (!provider) {
            throw new Error(`Unknown authentication provider: ${method}`)
        }

        return provider
    }

    /**
     * Get list of enabled providers based on environment config
     * @returns {Array<string>} Array of enabled provider method names
     */
    getEnabled() {
        const defaultProviders = [
            AuthProviders.PASSWORD,
            AuthProviders.EMAIL_TOKEN,
            AuthProviders.GOOGLE,
            AuthProviders.FACEBOOK,
            AuthProviders.APPLE
        ].join(',')

        const configuredProviders = process.env.VUE_APP_AUTH_PROVIDERS || defaultProviders
        const providers = configuredProviders.split(',').map(p => p.trim()).filter(Boolean)

        return providers
    }

    /**
     * Check if social login providers are enabled
     * @returns {boolean}
     */
    hasSocial() {
        const enabled = this.getEnabled()
        return SocialProviders.some(provider => enabled.includes(provider))
    }

    /**
     * Check if a specific provider is enabled
     * @param {string} method - Provider method name
     * @returns {boolean}
     */
    isEnabled(method) {
        return this.getEnabled().includes(method)
    }

    /**
     * Get the primary provider based on priority and what's enabled
     * @returns {string} Primary provider method name
     */
    getPrimary() {
        const enabled = this.getEnabled()

        for (const provider of ProviderPriority) {
            if (enabled.includes(provider)) {
                return provider
            }
        }

        return AuthProviders.PASSWORD // Fallback
    }

}

// Export singleton instance
export default new AuthProviderFactory()
